import { Checkbox } from '@mantine/core';
import { useState } from 'react';
import Header from '../../../components/Header';
import SnippetBox from '../../../components/SnippetBox';
import { SnippetContainerBox } from '../../../components/SnippetContainer';
import IconGlobe from '../../../components/icons/IconGlobe';
import { ROUTES } from '../../../routes/routes';
import { GatedContentConfiguration } from '../../../types/gatedContent';
import { SNIPPET_SERVER_URL } from '../../../utils/constants';
import Footer from '../../PricingPages/NewPage/Footer';
import { Pill } from '../../../components/Pill';
import HtmlTagName from '../../../components/HtmlTagName';

interface Props {
    gatedContent: GatedContentConfiguration;
}

export default function ViewSnippet({ gatedContent }: Props) {
    const [rememberMe, setRememberMe] = useState(false);
    const [customerKnown, setCustomerKnown] = useState(false);
    const urls = gatedContent.gatedDomains.flatMap((domain) =>
        gatedContent.gatedPaths.map(({ path, matchType }) => ({ url: `https://${domain}${path}`, matchType }))
    );
    const attributes: { [key: string]: string | null } = {
        'data-id': gatedContent.id,
        'data-remember-me': rememberMe ? 'true' : null,
        'data-email': customerKnown ? 'customer@example.com' : null,
        src: `${SNIPPET_SERVER_URL}/gated-content.js`,
    };
    const snippet = `<script ${Object.keys(attributes)
        .filter((key) => attributes[key] != null)
        .map((key) => `${key}="${attributes[key]}"`)
        .join(' ')}></script>`;
    return (
        <>
            <div className="flex flex-col w-2/3 mx-auto">
                <Header className="text-center py-8 mb-8">Your Gated Content is ready to embed</Header>
                <SnippetContainerBox>
                    <p className="pb-2">
                        Add the snippet to the <HtmlTagName name="head" /> tag of your HTML for the following pages:
                    </p>
                    <ul className="font-light py-4">
                        {urls.map(({ url, matchType }) => (
                            <li key={url} className="border rounded-xl bg-gray-50 px-4 py-2">
                                <IconGlobe className="w-6 inline pr-2" />
                                <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                    {url}
                                </a>
                                {matchType === 'WILDCARD' && (
                                    <span className="pl-2 italic">👈 any url that starts with this</span>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-4 underline">Snippet Options</div>

                    <Checkbox
                        label="Remember customer login for 90 days"
                        className="mt-4"
                        checked={rememberMe}
                        onChange={(event) => setRememberMe(event.currentTarget.checked)}
                    />

                    <Checkbox
                        label="Customer email is already known (e.g. logged in)"
                        className="mt-4"
                        checked={customerKnown}
                        onChange={(event) => setCustomerKnown(event.currentTarget.checked)}
                    />

                    <div className="py-4">Copy the snippet below 👇🏻</div>
                    <SnippetBox snippet={snippet} />

                    {customerKnown && (
                        <div className="pt-8">
                            <Pill title="Tip 💡" colour="YELLOW" />
                            <p className="pt-2">
                                Replace <pre className="inline bg-gray-100">customer@example.com</pre> with the email
                                address of your customer.
                            </p>
                        </div>
                    )}
                </SnippetContainerBox>
            </div>
            <Footer
                prev={{ href: ROUTES.GATED_CONTENT_EDIT, disabled: false, label: 'Configure' }}
                item={gatedContent}
                size="LARGE"
            />
        </>
    );
}
